import type { IDeliverableStatus } from "./deliverables/types";
import type { IStatusView } from "./types";

export enum RaasMilestoneBackendStatus {
  NotStarted = "not_started",
  InProgress = "inprogress",
  SubmittedToClient = "submitted_to_client",
  Approved = "approved",
}

export enum RaasProjectListBackendStatus {
  NotStarted = "Not Started",
  InProgress = "In Progress",
  SubmittedToClient = "Submitted to Client",
  Approved = "Approved",
  Completed = "Completed",
}

export enum RaasCanonicalStatus {
  NotStarted = "not_started",
  InProgress = "in_progress",
  SubmittedToClient = "submitted_to_client",
  Approved = "approved",
  Completed = "completed",
  Locked = "locked",
  Unavailable = "unavailable",
  Unknown = "unknown",
}

export enum RaasStatusAudience {
  Client = "client",
  Internal = "internal",
}

const STATUS_WHITESPACE_PATTERN = /\s+/g;

export const normalizeRaasStatus = ({
  value,
}: {
  value: unknown;
}): RaasCanonicalStatus => {
  if (typeof value !== "string") return RaasCanonicalStatus.Unknown;

  const normalizedValue = value
    .trim()
    .toLowerCase()
    .replace(STATUS_WHITESPACE_PATTERN, "_");

  switch (normalizedValue) {
    case "not_started":
    case "notstarted":
      return RaasCanonicalStatus.NotStarted;
    case "inprogress":
    case "in_progress":
      return RaasCanonicalStatus.InProgress;
    case "submitted_to_client":
      return RaasCanonicalStatus.SubmittedToClient;
    case "approved":
    case "client_approved":
      return RaasCanonicalStatus.Approved;
    case "completed":
      return RaasCanonicalStatus.Completed;
    default:
      return RaasCanonicalStatus.Unknown;
  }
};

const getSubmittedStatusLabel = ({
  audience,
}: {
  audience: RaasStatusAudience;
}) =>
  audience === RaasStatusAudience.Internal
    ? "Submitted to Client"
    : "Ready for Approval";

export const getRaasProjectListStatusView = ({
  audience,
  value,
}: {
  audience: RaasStatusAudience;
  value: unknown;
}): IStatusView => {
  const status = normalizeRaasStatus({ value });

  switch (status) {
    case RaasCanonicalStatus.InProgress:
      return { status: "in_progress", label: "In Progress", tone: "blue" };
    case RaasCanonicalStatus.SubmittedToClient:
      return {
        status: "submitted_to_client",
        label: getSubmittedStatusLabel({ audience }),
        tone: "yellow",
      };
    case RaasCanonicalStatus.Approved:
    case RaasCanonicalStatus.Completed:
      return { status: "completed", label: "Approved", tone: "green" };
    case RaasCanonicalStatus.NotStarted:
    case RaasCanonicalStatus.Unknown:
    default:
      return {
        status: "not_started",
        label: "Not Started",
        tone: "grey",
        muted: true,
      };
  }
};

export const getRaasDeliverableStatusView = ({
  audience,
  value,
}: {
  audience: RaasStatusAudience;
  value: unknown;
}): IDeliverableStatus | undefined => {
  const status = normalizeRaasStatus({ value });

  switch (status) {
    case RaasCanonicalStatus.InProgress:
      return { label: "In Progress", status: "ready", tone: "blue" };
    case RaasCanonicalStatus.NotStarted:
      return { label: "Not Started", status: "not_started", tone: "grey" };
    case RaasCanonicalStatus.SubmittedToClient:
      return {
        label: getSubmittedStatusLabel({ audience }),
        status: "ready",
        tone: "yellow",
      };
    case RaasCanonicalStatus.Approved:
    case RaasCanonicalStatus.Completed:
      return { label: "Approved", status: "approved", tone: "green" };
    case RaasCanonicalStatus.Unknown:
    default:
      return undefined;
  }
};
